import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Select';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Download,
  Building2,
  Clock,
  FileText
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatPercentage } from '@/lib/formatters';
import { useAuthStore } from '@/stores/authStore';
import { PaymentHistoryService } from '@/services/paymentHistoryService';

interface DashboardStats {
  totalTitulos: number;
  titulosAbertos: number;
  titulosVencidos: number;
  titulosParciais: number;
  titulosFechados: number;
  valorTotal: number;
  valorVencido: number;
  valorParcial: number;
  valorFechado: number;
  operadoresAtivos: number;
  empresasAtivas: number;
  titulosPorStatus: Array<{
    status: string;
    count: number;
    valor: number;
  }>;
  titulosPorOperador: Array<{
    operador: string;
    count: number;
    valor: number;
  }>;
  titulosPorEmpresa: Array<{
    empresa: string;
    count: number;
    valor: number;
  }>;
}

interface OperatorPerformance {
  operador: string;
  titulosAtribuidos: number;
  titulosFechados: number;
  valorRecuperado: number;
  taxaSucesso: number;
  mediaTempoFechamento: number;
}

interface AgingReport {
  faixa: string;
  count: number;
  valor: number;
  percentual: number;
}

export const ReportsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [operatorsPerformance, setOperatorsPerformance] = useState<OperatorPerformance[]>([]);
  const [agingReport, setAgingReport] = useState<AgingReport[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [conversionKpis, setConversionKpis] = useState<{ total: number; confirmados: number; parciais: number; taxa: number } | null>(null);
  
  // Estados para filtros
  const [selectedOperador, setSelectedOperador] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30');
  const [operadores, setOperadores] = useState<any[]>([]);
  
  // Estados para modal de aging
  const [showAgingModal, setShowAgingModal] = useState(false);
  const [selectedAgingFaixa, setSelectedAgingFaixa] = useState<string>('');
  const [agingClientes, setAgingClientes] = useState<any[]>([]);
  const [loadingAgingClientes, setLoadingAgingClientes] = useState(false);

  // Estados para relatório de produtividade
  const [activeTab, setActiveTab] = useState<'dashboard' | 'operators' | 'aging' | 'productivity' | 'feedbacks' | 'kpis'>('dashboard');
  const [productivityData, setProductivityData] = useState<any>(null);
  const [feedbacksData, setFeedbacksData] = useState<any>(null);
  const [feedbacksDetails, setFeedbacksDetails] = useState<any[]>([]);
  const [loadingProductivity, setLoadingProductivity] = useState(false);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedTipo, setSelectedTipo] = useState<string>('');
  const [showFeedbacksModal, setShowFeedbacksModal] = useState(false);

  // Estados para KPIs dos operadores
  const [kpisData, setKpisData] = useState<any>(null);
  const [loadingKpis, setLoadingKpis] = useState(false);

  const loadReports = async () => {
    try {
      setLoading(true);
      
      // Construir parâmetros de filtro
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (selectedPeriod) params.append('period', selectedPeriod);
      
      const [dashboardResponse, operatorsResponse, agingResponse, companiesResponse, operadoresResponse] = await Promise.all([
        api.get(`/reports/dashboard?${params.toString()}`),
        api.get(`/reports/operators/performance?${params.toString()}`),
        api.get(`/reports/aging?${params.toString()}`),
        api.get('/companies?isActive=true'),
        api.get('/users?role=OPERADOR&isActive=true')
      ]);
      
      setDashboardStats(dashboardResponse.data.data);
      setOperatorsPerformance(operatorsResponse.data.data.operators || []);
      setAgingReport(agingResponse.data.data.aging || []);
      setCompanies(companiesResponse.data.data.companies || []);
      setOperadores(operadoresResponse.data.data.users || []);

      // KPIs de conversão de negociações -> pagamentos
      if (selectedCompany) {
        const conv = await PaymentHistoryService.getConversionRate({ companyId: selectedCompany });
        setConversionKpis({
          total: conv.totalNegociacoes,
          confirmados: conv.pagamentosConfirmados,
          parciais: conv.pagamentosParciais,
          taxa: conv.taxaConversao,
        });
      } else {
        setConversionKpis(null);
      }
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const loadProductivityReport = async () => {
    try {
      setLoadingProductivity(true);
      
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/reports/productivity?${params.toString()}`);
      setProductivityData(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório de produtividade');
    } finally {
      setLoadingProductivity(false);
    }
  };

  const loadFeedbacksReport = async () => {
    try {
      setLoadingFeedbacks(true);
      
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedTipo) params.append('tipo', selectedTipo);
      
      const response = await api.get(`/reports/feedbacks?${params.toString()}`);
      setFeedbacksData(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório de feedbacks');
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const loadFeedbacksDetails = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedTipo) params.append('tipo', selectedTipo);
      
      const response = await api.get(`/reports/feedbacks/details?${params.toString()}`);
      setFeedbacksDetails(response.data.data.feedbacks || []);
      setShowFeedbacksModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes dos feedbacks');
    }
  };

  const loadKPIs = async () => {
    try {
      setLoadingKpis(true);
      
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/reports/operator-kpis?${params.toString()}`);
      setKpisData(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar KPIs dos operadores');
    } finally {
      setLoadingKpis(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  // Recarregar relatórios quando filtros mudarem
  useEffect(() => {
    if (!loading) {
      loadReports();
    }
  }, [selectedCompany, selectedOperador, selectedPeriod]);

  // Carregar relatórios específicos quando abas mudarem
  useEffect(() => {
    if (activeTab === 'productivity' && !productivityData) {
      loadProductivityReport();
    }
    if (activeTab === 'feedbacks' && !feedbacksData) {
      loadFeedbacksReport();
    }
    if (activeTab === 'kpis' && !kpisData) {
      loadKPIs();
    }
  }, [activeTab]);

  // Recarregar relatórios de produtividade quando filtros mudarem
  useEffect(() => {
    if (activeTab === 'productivity' && productivityData) {
      loadProductivityReport();
    }
  }, [selectedCompany, selectedOperador, startDate, endDate]);

  // Recarregar relatórios de feedbacks quando filtros mudarem
  useEffect(() => {
    if (activeTab === 'feedbacks' && feedbacksData) {
      loadFeedbacksReport();
    }
  }, [selectedCompany, selectedOperador, startDate, endDate, selectedTipo]);

  // Recarregar KPIs quando filtros mudarem
  useEffect(() => {
    if (activeTab === 'kpis' && kpisData) {
      loadKPIs();
    }
  }, [selectedCompany, selectedOperador, startDate, endDate]);

  const loadAgingClientes = async (faixa: string) => {
    try {
      setLoadingAgingClientes(true);
      setSelectedAgingFaixa(faixa);
      
      const params = new URLSearchParams();
      params.append('faixa', faixa);
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedOperador) params.append('operadorId', selectedOperador);
      if (selectedPeriod) params.append('period', selectedPeriod);
      
      const response = await api.get(`/reports/aging/clientes?${params.toString()}`);
      setAgingClientes(response.data.data.clientes || []);
      setShowAgingModal(true);
    } catch (error) {
      toast.error('Erro ao carregar clientes do aging');
    } finally {
      setLoadingAgingClientes(false);
    }
  };

  const handleExportReport = async (type: 'dashboard' | 'operators' | 'aging' | 'company') => {
    try {
      setExporting(true);
      
      let endpoint = '/reports/export';
      let params: any = { type };
      
      if (type === 'company' && selectedCompany) {
        params.companyId = selectedCompany;
      }
      
      const response = await api.post(endpoint, params, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  // Usar formatters importados

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando relatórios...</div>
      </div>
    );
  }

  return (
          <div className="space-y-6" data-testid="page-relatorios">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Relatórios de Cobrança</h1>
          <p className="text-muted-foreground">Análise completa do desempenho da cobrança</p>
          {/* Indicador de escopo */}
          <div className="mt-2">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <span>
                {user?.role === 'OPERADOR' 
                  ? '📊 Relatórios dos seus títulos' 
                  : user?.role === 'GESTOR'
                  ? '📊 Relatórios da sua empresa'
                  : '📊 Relatórios do sistema'
                }
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button onClick={() => handleExportReport('dashboard')} disabled={exporting}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Dashboard
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                Empresa
              </label>
              <Select
                value={selectedCompany}
                onValueChange={setSelectedCompany}
                placeholder="Todas as empresas"
              >
                <option value="">Todas as empresas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                Operador
              </label>
              <Select
                value={selectedOperador}
                onValueChange={setSelectedOperador}
                placeholder="Todos os operadores"
              >
                <option value="">Todos os operadores</option>
                {operadores.map((operador) => (
                  <option key={operador.id} value={operador.id}>
                    {operador.name}
                  </option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                Período (dias)
              </label>
              <Select
                value={selectedPeriod}
                onValueChange={setSelectedPeriod}
              >
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="60">Últimos 60 dias</option>
                <option value="90">Últimos 90 dias</option>
                <option value="180">Últimos 180 dias</option>
                <option value="365">Último ano</option>
              </Select>
            </div>
          </div>

          {/* Filtros específicos para produtividade, feedbacks e KPIs */}
          {(activeTab === 'productivity' || activeTab === 'feedbacks' || activeTab === 'kpis') && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                  Data Início
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {activeTab === 'feedbacks' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 text-gray-700 dark:text-slate-200 mb-2">
                    Tipo de Contato
                  </label>
                  <Select
                    value={selectedTipo}
                    onValueChange={setSelectedTipo}
                    placeholder="Todos os tipos"
                  >
                    <option value="">Todos os tipos</option>
                    <option value="CONTATO">Contato</option>
                    <option value="LIGACAO">Ligação</option>
                    <option value="EMAIL">Email</option>
                    <option value="WHATSAPP">WhatsApp</option>
                    <option value="VISITA">Visita</option>
                    <option value="NEGOCIACAO">Negociação</option>
                    <option value="PROMESSA">Promessa</option>
                  </Select>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Abas de Navegação */}
      <div className="border-b border-gray-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('operators')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'operators'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Operadores
          </button>
          <button
            onClick={() => setActiveTab('aging')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'aging'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Aging
          </button>
          <button
            onClick={() => setActiveTab('productivity')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'productivity'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Produtividade
          </button>
          <button
            onClick={() => setActiveTab('feedbacks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'feedbacks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Feedbacks
          </button>
          <button
            onClick={() => setActiveTab('kpis')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'kpis'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:text-slate-200 hover:border-gray-300'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            KPIs Operadores
          </button>
        </nav>
      </div>

      {/* Conteúdo da aba Dashboard */}
      {activeTab === 'dashboard' && dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Títulos</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardStats.totalTitulos}</div>
              <p className="text-xs text-muted-foreground">
                {dashboardStats.empresasAtivas} empresas ativas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(dashboardStats.valorTotal)}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(dashboardStats.valorVencido)} vencidos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Operadores Ativos</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboardStats.operadoresAtivos}</div>
              <p className="text-xs text-muted-foreground">
                Distribuindo títulos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Recuperação</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboardStats.totalTitulos > 0 
                  ? formatPercentage((dashboardStats.titulosFechados / dashboardStats.totalTitulos) * 100)
                  : '0%'
                }
              </div>
              <p className="text-xs text-muted-foreground">
                {dashboardStats.titulosFechados} títulos fechados
              </p>
            </CardContent>
          </Card>

          {/* KPIs de Conversão de Negociações */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversão de Negociações</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {conversionKpis ? (
                <>
                  <div className="text-2xl font-bold">{formatPercentage(conversionKpis.taxa)}</div>
                  <p className="text-xs text-muted-foreground">
                    {conversionKpis.confirmados} confirmados, {conversionKpis.parciais} parciais de {conversionKpis.total} negociações
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Selecione uma empresa para ver</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Títulos por Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Títulos por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboardStats?.titulosPorStatus.map((item) => (
                <div key={item.status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-blue-100 text-blue-800">
                      {item.status}
                    </Badge>
                    <span className="text-sm font-medium">{item.count}</span>
                  </div>
                  <span className="text-sm font-medium">{formatCurrency(item.valor)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Performance dos Operadores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Performance dos Operadores
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExportReport('operators')}
                disabled={exporting}
              >
                <Download className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {operatorsPerformance.map((operator) => (
                <div key={operator.operador} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{operator.operador}</span>
                    <Badge className="bg-green-100 text-green-800">
                      {formatPercentage(operator.taxaSucesso)}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-slate-300">Atribuídos:</span>
                      <span className="ml-1 font-medium">{operator.titulosAtribuidos}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-slate-300">Fechados:</span>
                      <span className="ml-1 font-medium">{operator.titulosFechados}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-slate-300">Valor:</span>
                      <span className="ml-1 font-medium">{formatCurrency(operator.valorRecuperado)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Aging Report */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Relatório de Aging
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleExportReport('aging')}
              disabled={exporting}
            >
              <Download className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agingReport.map((item) => (
              <div 
                key={item.faixa} 
                className="border rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 dark:bg-slate-800 transition-colors"
                onClick={() => loadAgingClientes(item.faixa)}
              >
                <div className="text-lg font-bold text-blue-600">{item.faixa}</div>
                <div className="text-2xl font-bold">{item.count}</div>
                <div className="text-sm text-gray-600 dark:text-slate-300">{formatCurrency(item.valor)}</div>
                <div className="text-xs text-gray-500 dark:text-slate-400">{formatPercentage(item.percentual)}</div>
                <div className="text-xs text-blue-500 mt-2">Clique para ver detalhes</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Company Report */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Relatório por Empresa
            </div>
            <div className="flex items-center space-x-2">
              <Select
                value={selectedCompany}
                onValueChange={setSelectedCompany}
                className="w-48"
              >
                <option value="">Selecione uma empresa</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExportReport('company')}
                disabled={exporting || !selectedCompany}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedCompany ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-slate-400">Relatório detalhado da empresa selecionada</p>
              <p className="text-sm text-gray-400 mt-2">
                Clique em "Exportar" para baixar o relatório completo
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-slate-400">Selecione uma empresa para ver o relatório detalhado</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conteúdo da aba Operadores */}
      {activeTab === 'operators' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Performance dos Operadores
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleExportReport('operators')}
                  disabled={exporting}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {operatorsPerformance.map((operator) => (
                  <div key={operator.operador} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{operator.operador}</span>
                      <Badge className="bg-green-100 text-green-800">
                        {formatPercentage(operator.taxaSucesso)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-slate-300">Atribuídos:</span>
                        <span className="ml-1 font-medium">{operator.titulosAtribuidos}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-slate-300">Fechados:</span>
                        <span className="ml-1 font-medium">{operator.titulosFechados}</span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-slate-300">Valor:</span>
                        <span className="ml-1 font-medium">{formatCurrency(operator.valorRecuperado)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conteúdo da aba Aging */}
      {activeTab === 'aging' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Relatório de Aging
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleExportReport('aging')}
                  disabled={exporting}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {agingReport.map((item) => (
                  <div 
                    key={item.faixa} 
                    className="border rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 dark:bg-slate-800 transition-colors"
                    onClick={() => loadAgingClientes(item.faixa)}
                  >
                    <div className="text-lg font-bold text-blue-600">{item.faixa}</div>
                    <div className="text-2xl font-bold">{item.count}</div>
                    <div className="text-sm text-gray-600 dark:text-slate-300">{formatCurrency(item.valor)}</div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">{formatPercentage(item.percentual)}</div>
                    <div className="text-xs text-blue-500 mt-2">Clique para ver detalhes</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Conteúdo da aba Produtividade */}
      {activeTab === 'productivity' && (
        <div className="space-y-6">
          {loadingProductivity ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg">Carregando relatório de produtividade...</div>
            </div>
          ) : productivityData ? (
            <>
              {/* KPIs de Produtividade */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total de Feedbacks</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{productivityData.totalFeedbacks}</div>
                    <p className="text-xs text-muted-foreground">
                      {productivityData.mediaFeedbacksPorDia} por dia
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{productivityData.taxaSucesso}%</div>
                    <p className="text-xs text-muted-foreground">
                      Títulos fechados
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Valor Recuperado</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatCurrency(productivityData.valorRecuperado)}</div>
                    <p className="text-xs text-muted-foreground">
                      No período
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Contatos Realizados</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{productivityData.totalContatos}</div>
                    <p className="text-xs text-muted-foreground">
                      {productivityData.totalLigacoes} ligações, {productivityData.totalWhatsapp} WhatsApp
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Feedbacks por Tipo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Feedbacks por Tipo de Contato
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {productivityData.feedbacksPorTipo.map((item: any) => (
                      <div key={item.tipo} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-blue-100 text-blue-800">
                            {item.tipo}
                          </Badge>
                          <span className="text-sm font-medium">{item.quantidade}</span>
                        </div>
                        <span className="text-sm font-medium">{item.percentual}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Produtividade por Operador */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Produtividade por Operador
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {productivityData.feedbacksPorOperador.map((operador: any) => (
                      <div key={operador.operador} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium">{operador.operador}</span>
                            <p className="text-sm text-gray-600 dark:text-slate-300">{operador.email}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800">
                            {operador.totalFeedbacks} feedbacks
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Média: {operador.mediaPorDia} feedbacks por dia
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-slate-400">Selecione um período para ver o relatório de produtividade</p>
            </div>
          )}
        </div>
      )}

      {/* Conteúdo da aba Feedbacks */}
      {activeTab === 'feedbacks' && (
        <div className="space-y-6">
          {loadingFeedbacks ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg">Carregando relatório de feedbacks...</div>
            </div>
          ) : feedbacksData ? (
            <>
              {/* KPIs de Feedbacks */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total de Feedbacks</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{feedbacksData.totalFeedbacks}</div>
                    <p className="text-xs text-muted-foreground">
                      No período selecionado
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Com Áudio</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{feedbacksData.percentualComAudio}%</div>
                    <p className="text-xs text-muted-foreground">
                      {feedbacksData.feedbacksComAudio} feedbacks
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Próximo Contato</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{feedbacksData.percentualComProximoContato}%</div>
                    <p className="text-xs text-muted-foreground">
                      {feedbacksData.feedbacksComProximoContato} feedbacks
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Operadores Ativos</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{feedbacksData.feedbacksPorOperador.length}</div>
                    <p className="text-xs text-muted-foreground">
                      Registrando feedbacks
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Feedbacks por Tipo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2" />
                      Feedbacks por Tipo
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={loadFeedbacksDetails}
                    >
                      Ver Detalhes
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {feedbacksData.feedbacksPorTipo.map((item: any) => (
                      <div key={item.tipo} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-blue-100 text-blue-800">
                            {item.tipo}
                          </Badge>
                          <span className="text-sm font-medium">{item.quantidade}</span>
                        </div>
                        <span className="text-sm font-medium">{item.percentual}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Operadores */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Top Operadores
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {feedbacksData.feedbacksPorOperador.slice(0, 5).map((operador: any) => (
                      <div key={operador.operador} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{operador.operador}</span>
                            <p className="text-sm text-gray-600 dark:text-slate-300">{operador.email}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800">
                            {operador.totalFeedbacks} feedbacks
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top Clientes */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Building2 className="h-5 w-5 mr-2" />
                    Top Clientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {feedbacksData.feedbacksPorCliente.map((cliente: any) => (
                      <div key={cliente.cliente} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{cliente.cliente}</span>
                            <p className="text-sm text-gray-600 dark:text-slate-300">Código: {cliente.codigo}</p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800">
                            {cliente.totalFeedbacks} feedbacks
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-slate-400">Selecione um período para ver o relatório de feedbacks</p>
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalhes dos Feedbacks */}
      {showFeedbacksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Detalhes dos Feedbacks
              </h2>
              <Button
                variant="outline"
                onClick={() => setShowFeedbacksModal(false)}
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-4">
              {feedbacksDetails.map((feedback) => (
                <div key={feedback.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{feedback.clienteFornecedor?.nome || 'Cliente não identificado'}</h3>
                      <p className="text-sm text-gray-600 dark:text-slate-300">Código: {feedback.clienteFornecedor?.codigo || 'N/A'}</p>
                      <p className="text-sm text-gray-600 dark:text-slate-300">Operador: {feedback.user?.name || 'Não identificado'}</p>
                      <p className="text-sm text-gray-600 dark:text-slate-300">Email: {feedback.user?.email || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-blue-100 text-blue-800 mb-2">
                        {feedback.tipo}
                      </Badge>
                      <div className="text-sm text-gray-600 dark:text-slate-300">
                        {new Date(feedback.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-slate-300">
                        {new Date(feedback.createdAt).toLocaleTimeString('pt-BR')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <h4 className="font-medium text-gray-700 dark:text-slate-200 mb-1">Descrição:</h4>
                    <p className="text-sm text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 p-2 rounded">
                      {feedback.descricao}
                    </p>
                  </div>

                  {feedback.tipoNegociacao && (
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-700 dark:text-slate-200 mb-1">Tipo de Negociação:</h4>
                      <Badge className="bg-green-100 text-green-800">
                        {feedback.tipoNegociacao}
                      </Badge>
                    </div>
                  )}

                  {feedback.proximoContato && (
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-700 dark:text-slate-200 mb-1">Próximo Contato:</h4>
                      <p className="text-sm text-gray-600 dark:text-slate-300">
                        {new Date(feedback.proximoContato).toLocaleDateString('pt-BR')} às {new Date(feedback.proximoContato).toLocaleTimeString('pt-BR')}
                      </p>
                    </div>
                  )}

                  {feedback.audio && (
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-700 dark:text-slate-200 mb-1">Áudio:</h4>
                      <p className="text-sm text-gray-600 dark:text-slate-300">
                        {feedback.audio.fileName}
                      </p>
                    </div>
                  )}

                  {feedback.titulo && (
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-700 dark:text-slate-200 mb-1">Título Relacionado:</h4>
                      <div className="bg-gray-50 dark:bg-slate-800 p-2 rounded">
                        <p className="text-sm text-gray-600 dark:text-slate-300">Número: {feedback.titulo.numero}</p>
                        <p className="text-sm text-gray-600 dark:text-slate-300">Valor: {formatCurrency(feedback.titulo.valor)}</p>
                        <p className="text-sm text-gray-600 dark:text-slate-300">Status: {feedback.titulo.status}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Aging */}
      {showAgingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Clientes - {selectedAgingFaixa}
              </h2>
              <Button
                variant="outline"
                onClick={() => setShowAgingModal(false)}
              >
                ✕
              </Button>
            </div>
            
            {loadingAgingClientes ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-lg">Carregando clientes...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {agingClientes.map((cliente) => (
                  <div key={cliente.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{cliente.nome}</h3>
                        <p className="text-sm text-gray-600 dark:text-slate-300">{cliente.documento}</p>
                        <p className="text-sm text-gray-600 dark:text-slate-300">{cliente.email}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-600">
                          {formatCurrency(cliente.valorTotal)}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          {cliente.quantidadeTitulos} títulos
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div className="bg-red-50 p-2 rounded">
                        <div className="font-medium text-red-700">Vencidos</div>
                        <div>{cliente.titulosVencidos} títulos</div>
                        <div className="text-red-600">{formatCurrency(cliente.valorVencido)}</div>
                      </div>
                      <div className="bg-yellow-50 p-2 rounded">
                        <div className="font-medium text-yellow-700">Abertos</div>
                        <div>{cliente.titulosAbertos} títulos</div>
                        <div className="text-yellow-600">{formatCurrency(cliente.valorAberto)}</div>
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <div className="font-medium text-green-700">Parciais</div>
                        <div>{cliente.titulosParciais} títulos</div>
                        <div className="text-green-600">{formatCurrency(cliente.valorParcial)}</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-slate-800 p-2 rounded">
                        <div className="font-medium text-gray-700 dark:text-slate-200">Fechados</div>
                        <div>{cliente.titulosFechados} títulos</div>
                        <div className="text-gray-600 dark:text-slate-300">{formatCurrency(cliente.valorFechado)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conteúdo da aba KPIs Operadores */}
      {activeTab === 'kpis' && (
        <div className="space-y-6">
          {loadingKpis ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg">Carregando KPIs dos operadores...</div>
            </div>
          ) : kpisData ? (
            <>
              {/* Resumo do Período */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  Período de Análise
                </h3>
                <p className="text-blue-700 dark:text-blue-300">
                  {kpisData.periodo.inicio} até {kpisData.periodo.fim} 
                  ({kpisData.periodo.diasTrabalhados} dias)
                </p>
              </div>

              {/* Cards de KPIs por Operador */}
              <div className="space-y-6">
                {kpisData.kpis.map((operador: any, index: number) => (
                  <div key={operador.operadorId} className="border rounded-lg p-6 bg-white dark:bg-slate-800">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {operador.operador}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-slate-300">
                          Ranking: #{index + 1}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Títulos Atribuídos: {operador.dadosAdicionais.titulosAtribuidos}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Títulos Fechados: {operador.dadosAdicionais.titulosFechados}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Valor Recuperado: {formatCurrency(operador.dadosAdicionais.valorRecuperado)}
                        </div>
                      </div>
                    </div>

                    {/* Grid de KPIs */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* KPI 3: Tempo Médio de Resolução */}
                      <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            Tempo Médio de Resolução
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            operador.kpi3_tempoMedioResolucao.status === 'bom' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : operador.kpi3_tempoMedioResolucao.status === 'regular'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {operador.kpi3_tempoMedioResolucao.status === 'bom' ? 'Bom' : 
                             operador.kpi3_tempoMedioResolucao.status === 'regular' ? 'Regular' : 'Ruim'}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {operador.kpi3_tempoMedioResolucao.valor} {operador.kpi3_tempoMedioResolucao.unidade}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Meta: {operador.kpi3_tempoMedioResolucao.meta} {operador.kpi3_tempoMedioResolucao.unidade}
                        </div>
                      </div>

                      {/* KPI 4: Volume de Atividades */}
                      <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            Volume de Atividades
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            operador.kpi4_volumeAtividades.status === 'bom' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : operador.kpi4_volumeAtividades.status === 'regular'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          }`}>
                            {operador.kpi4_volumeAtividades.status === 'bom' ? 'Bom' : 
                             operador.kpi4_volumeAtividades.status === 'regular' ? 'Regular' : 'Ruim'}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {operador.kpi4_volumeAtividades.valor} {operador.kpi4_volumeAtividades.unidade}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Total: {operador.kpi4_volumeAtividades.totalFeedbacks} feedbacks
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Meta: {operador.kpi4_volumeAtividades.meta} {operador.kpi4_volumeAtividades.unidade}
                        </div>
                      </div>

                      {/* KPI 5: Taxa de Sucesso por Contato */}
                      <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            Taxa de Sucesso Geral
                          </h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            operador.kpi5_taxaSucessoContato.status === 'bom' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {operador.kpi5_taxaSucessoContato.status === 'bom' ? 'Bom' : 'Regular'}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {operador.kpi5_taxaSucessoContato.mediaGeral} {operador.kpi5_taxaSucessoContato.unidade}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-slate-300">
                          Meta: {operador.kpi5_taxaSucessoContato.meta} {operador.kpi5_taxaSucessoContato.unidade}
                        </div>
                      </div>
                    </div>

                    {/* Detalhamento por Tipo de Contato */}
                    {operador.kpi5_taxaSucessoContato.tipos.length > 0 && (
                      <div className="mt-6">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                          Taxa de Sucesso por Tipo de Contato
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {operador.kpi5_taxaSucessoContato.tipos.map((tipo: any) => (
                            <div key={tipo.tipo} className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-blue-900 dark:text-blue-200">
                                  {tipo.tipo}
                                </span>
                                <span className="text-sm text-blue-700 dark:text-blue-300">
                                  {tipo.taxaSucesso}%
                                </span>
                              </div>
                              <div className="text-xs text-blue-600 dark:text-blue-300">
                                {tipo.contatosEfetivos}/{tipo.totalContatos} contatos efetivos
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-slate-400">
                Nenhum dado de KPI encontrado para o período selecionado.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
